import { useState } from 'react'
import type { JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { LNGS, FALLBACK_LNG, switchLanguage, type Lng } from './i18n/index.ts'
import { useHoverOpen } from './Select.tsx'

/**
 * ══ MỘT component, dùng ở BỐN chỗ — NGOẠI LỆ HỢP LỆ CỦA §7 (B6 · E8/D1, anh chốt 03/09) ════════
 *
 * Bản cũ có HAI cách đổi ngôn ngữ:
 *   · `Layout.tsx:706` — dropdown 3 ngôn ngữ (đủ)
 *   · `Login.tsx:147` và `PublicTracking.tsx:65` — nút 2 CHIỀU `vi↔en`
 * Với nút 2 chiều, người dùng 中文 đăng xuất ra là KHÔNG CÓ ĐƯỜNG QUAY LẠI 中文 (vi→en→vi→en…).
 * Trang tra cứu công khai còn nặng hơn: khách không đăng nhập được nên VĨNH VIỄN không xem được
 * bằng 中文 dù chuỗi đã dịch sẵn.
 *
 * ⚠ ĐÂY LÀ MỘT TRONG **HAI NGOẠI LỆ DUY NHẤT** của luật "không thêm không bớt" (§7) — B6 và B7,
 *   anh đã xác nhận lại 03/09 (`DIEM-CAN-QUYET-DINH.md` ▸ D1 · E8). **Đừng "sửa cho bám bản cũ".**
 *
 * Bốn nơi dùng: khung app (`AppLayout`) · `/login` · `/ncc/login` (màn MỚI của E15, spec D03 §6.1:
 * *"cùng khung và cùng `<LanguagePicker>`"*) · `/tra-cuu`. Ba nơi đầu trong bảng D03b §7.1 là ba
 * chỗ CÓ SẴN ở bản cũ; `/ncc/login` sinh ra cùng E15 nên không có bản cũ để bám.
 *
 * ✅ B7 (❓4, chốt 03/09): KHÔNG mang sang dòng chú thích ở `Layout.tsx:745`
 *    ("中文/EN mới dịch menu & nhãn chung; các trang nghiệp vụ vẫn tiếng Việt") — câu đó SAI SỰ THẬT,
 *    đã đo 9.480/9.480/9.480 khoá. Bỏ hẳn, không thay bằng câu khác.
 *
 * ⚠ NHÃN BA NGÔN NGỮ LÀ CHUỖI CỨNG, CỐ Ý — chép nguyên bảng `LANGS` (`Layout.tsx:706-710`). Tên
 *   ngôn ngữ viết bằng CHÍNH NÓ, không dịch: người đang kẹt ở giao diện 中文 phải đọc ra được chữ
 *   "Tiếng Việt" để quay về. Đưa vào kho chuỗi là làm ngược lại (và sinh 3 khoá mới không có ở bản
 *   cũ — §9 + luật P3).
 */
const LABELS: Readonly<Record<Lng, string>> = {
  vi: '🇻🇳 Tiếng Việt',
  en: '🇬🇧 English',
  zh: '🇨🇳 中文',
}

/** Khoá giữ TẠM lựa chọn của khách CHƯA đăng nhập. `auth.tsx` chuyển nó lên phiên sau khi vào. */
export const TEMP_LANG_KEY = 'rs:lang'

/** Cờ trên nút — chép `Layout.tsx:728` (cờ của ngôn ngữ đang dùng đứng trước mã). */
const FLAGS: Readonly<Record<Lng, string>> = { vi: '🇻🇳', en: '🇬🇧', zh: '🇨🇳' }

/** `tone="light"` cho trang nền sáng (`/login`, `/tra-cuu`); mặc định là header dải màu. */
export const LanguagePicker = ({ tone = 'header' }: { tone?: 'header' | 'light' } = {}): JSX.Element => {
  // `useTranslation()` để component VẼ LẠI khi ngôn ngữ đổi (kể cả khi đổi từ nơi khác — VD
  // `auth.tsx` áp `me.lang` ngay sau khi đăng nhập). Không dùng `t()` ở đây: xem chú thích NHAN.
  const { i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const current = (LNGS.includes(i18n.language as Lng) ? i18n.language : FALLBACK_LNG) as Lng
  /* ⭐ RÊ CHUỘT MỞ MENU — bản cũ `useHoverMenu` (`Layout.tsx:23-58`, user chốt 07/08) cho menu ngôn ngữ trên THANH
     TIÊU ĐỀ: máy có chuột rê vào là mở, rê ra (chạm lớp phủ) là đóng sau 220ms, đang mở do rê mà bấm thì GHIM. Trang
     nền sáng (`/login`, `/tra-cuu`) bản cũ là nút bấm 2 chiều — giữ bấm như cũ (L19, rà 16/10). */
  const hov = useHoverOpen(open, setOpen)
  const hover = tone === 'header'

  const pick = async (lng: Lng): Promise<void> => {
    hov.unpin()
    setOpen(false)
    // ⚠ `switchLanguage()` chứ KHÔNG phải `i18n.changeLanguage()` trần: nó NẠP TRƯỚC các nhóm khoá
    //   đang dùng cho ngôn ngữ mới RỒI mới đổi. Đổi trần thì màn vẽ lại ngay trong lúc kho chuỗi
    //   của ngôn ngữ mới chưa về — `missingKeyHandler` NÉM ở dev/test, và hiện khoá thô một nhịp
    //   ở production. KHÔNG tải lại trang (D03b §7.3).
    await switchLanguage(lng)

    // Đã đăng nhập ⇒ lưu vào PHIÊN + hồ sơ NGƯỜI DÙNG ở máy chủ (đổi máy vẫn giữ, D03b §7.3).
    // Chưa đăng nhập (401) hoặc mất mạng ⇒ giữ TẠM ở localStorage; `auth.tsx` đẩy lên phiên ngay
    // sau khi đăng nhập xong.
    const r = await fetch('/api/v1/prefs/lang', {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ lang: lng }),
    }).catch(() => null)
    if (!r || !r.ok) {
      try { localStorage.setItem(TEMP_LANG_KEY, lng) } catch { /* chế độ riêng tư: bỏ qua */ }
    }
  }

  /* Kiểu dáng chép `Layout.tsx:725-748` (viên tròn trên dải cam→xanh của header). Trên nền SÁNG
     (`/login`, `/tra-cuu`) viên trắng mờ có viền — chép `Login.tsx:152-158` (soi mobile 12/10/2026:
     trước đó nút là chữ trần "🌐 VI ▾" 16 px, lệch ở MỌI màn). */
  const pill = tone === 'light'
    ? 'bg-white/70 border border-slate-200 text-slate-600 hover:bg-white'
    : 'bg-white/20 hover:bg-white/30'
  return (
    <div className="relative" {...(hover ? hov.wrap : {})}>
      <button
        type="button"
        // Ba thứ tiếng trong một `title` — chép `Layout.tsx:707`: người đọc được thứ tiếng nào
        // trong ba thứ cũng hiểu nút này làm gì.
        title="Ngôn ngữ / Language / 语言"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => { if (hover) hov.onBtn(); else setOpen(!open) }}
        className={`relative z-50 text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1 ${pill}`}
      >
        {FLAGS[current]}
        <span>{current.toUpperCase()}</span>
        <span className={`text-[8px] transition-transform ${open ? 'rotate-180' : ''}`}>▼</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => { hov.unpin(); setOpen(false) }} {...(hover ? hov.backdrop : {})} />
          {/* Bẫy thứ 2 của bản cũ (24/08): khe `mt-1.5` giữa nút và bảng lộ lớp phủ ⇒ rê ngang qua là hẹn đóng; gắn
              `wrap` TRỰC TIẾP lên bảng để chạm vào bảng là huỷ hẹn ngay. */}
          <ul role="listbox" aria-label="Ngôn ngữ / Language / 语言" {...(hover ? hov.wrap : {})} className="absolute right-0 top-full mt-1.5 z-50 bg-white border border-slate-200 rounded-xl shadow-xl py-1 w-40 text-slate-700 overflow-hidden">
            {LNGS.map((l) => (
              <li
                key={l}
                role="option"
                aria-selected={l === current}
                className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs cursor-pointer hover:bg-sky-50 ${l === current ? 'font-bold text-sky-700 bg-sky-50/60' : ''}`}
                onClick={() => { void pick(l) }}
              >
                <span className="flex-1">{LABELS[l]}</span>
                {l === current && <span className="text-[10px]">{' ✓'}</span>}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}
